import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axiosClient from '../../api/axiosClient';
import InvoicePrintModal from '../../components/InvoicePrintModal';
import { useToast } from '../../context/ToastContext';

const PurchaseEntry = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);

  // Purchase Head
  const [purchaseNo, setPurchaseNo] = useState('');
  const [purchaseDate, setPurchaseDate] = useState(new Date().toISOString().slice(0, 10));
  const [vendorId, setVendorId] = useState('');
  const [supplierSearch, setSupplierSearch] = useState('');
  const [supplierMobile, setSupplierMobile] = useState('');
  const [showSupplierList, setShowSupplierList] = useState(false);
  const [showSupplierModal, setShowSupplierModal] = useState(false);
  const [showQuickAddSupplierModal, setShowQuickAddSupplierModal] = useState(false);
  const [quickSupplierName, setQuickSupplierName] = useState('');
  const [quickSupplierMobile, setQuickSupplierMobile] = useState('');
  const [quickSupplierAddress, setQuickSupplierAddress] = useState('');
  const [quickSupplierSaving, setQuickSupplierSaving] = useState(false);
  const [selectedSupplierObj, setSelectedSupplierObj] = useState(null);
  const [supplierBalance, setSupplierBalance] = useState(0);

  const [invoiceNo, setInvoiceNo] = useState('');
  const [invoiceType, setInvoiceType] = useState('1'); // 0 = Cash, 1 = Credit
  const [remark, setRemark] = useState('');

  // Line items
  const [items, setItems] = useState([]);
  const [editingItemIndex, setEditingItemIndex] = useState(null);

  const [currentItem, setCurrentItem] = useState({
    productId: '',
    productName: '',
    qty: 0,
    unit: 'KG',
    rate: 0
  });

  const [discPercent, setDiscPercent] = useState(0);
  const [discAmount, setDiscAmount] = useState(0);
  const [vat, setVat] = useState(0);

  const [printData, setPrintData] = useState(null);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [showProductList, setShowProductList] = useState(false);
  const supplierDropdownRef = useRef(null);
  const productDropdownRef = useRef(null);
  const productInputRef = useRef(null);
  const qtyInputRef = useRef(null);
  const rateInputRef = useRef(null);
  const [highlightedSupplierIndex, setHighlightedSupplierIndex] = useState(-1);
  const [highlightedProdIndex, setHighlightedProdIndex] = useState(-1);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (supplierDropdownRef.current && !supplierDropdownRef.current.contains(event.target)) {
        setShowSupplierList(false);
      }
      if (productDropdownRef.current && !productDropdownRef.current.contains(event.target)) {
        setShowProductList(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const resetFormToBlank = useCallback(async () => {
    try {
      const nextNoRes = await axiosClient.get('/purchases/next-no');
      if (nextNoRes.success) {
        setPurchaseNo(nextNoRes.nextPurchaseNo);
      }
    } catch (e) {
      console.error('Error fetching next purchase number:', e);
    }
    setPurchaseDate(new Date().toISOString().slice(0, 10));
    setVendorId('');
    setSupplierSearch('');
    setSupplierMobile('');
    setSelectedSupplierObj(null);
    setSupplierBalance(0);
    setInvoiceNo('');
    setInvoiceType('1');
    setRemark('');
    setItems([]);
    setEditingItemIndex(null);
    setCurrentItem({
      productId: '',
      productName: '',
      qty: 0,
      unit: 'KG',
      rate: 0
    });
    setDiscPercent(0);
    setDiscAmount(0);
    setVat(0);
    setError('');

    if (id) {
      navigate('/purchases/new', { replace: true });
    }
  }, [id, navigate]);

  useEffect(() => {
    const loadInitData = async () => {
      try {
        const [supRes, prodRes, nextNoRes] = await Promise.all([
          axiosClient.get('/accounts?type=SUPPLIER'),
          axiosClient.get('/products'),
          axiosClient.get('/purchases/next-no')
        ]);

        const supList = supRes.data || [];
        if (supRes.success) setSuppliers(supList);
        if (prodRes.success) setProducts(prodRes.data || []);
        if (nextNoRes.success && !id) setPurchaseNo(nextNoRes.nextPurchaseNo);

        if (id) {
          const purRes = await axiosClient.get(`/purchases/${id}`);
          if (purRes.success) {
            const p = purRes.data;
            setPurchaseNo(p.purchaseNo);
            setPurchaseDate(p.purchaseDate ? String(p.purchaseDate).slice(0, 10) : new Date().toISOString().slice(0, 10));
            setVendorId(p.vendorId);
            setInvoiceNo(p.InvoiceNo || '');
            setInvoiceType(String(p.invoiceType || 1));
            setDiscPercent(parseFloat(p.discPercent) || 0);
            setDiscAmount(parseFloat(p.discAmount) || 0);
            setVat(parseFloat(p.vat) || 0);
            setRemark(p.Remark || '');

            const matchedSup = supList.find((s) => String(s.Id) === String(p.vendorId));
            if (matchedSup) {
              setSupplierSearch(matchedSup.AccountName);
              setSupplierMobile(p.SupplierMobile || matchedSup.MobileNo || '');
              setSelectedSupplierObj(matchedSup);
            } else if (p.VendorName || p.SupplierName) {
              setSupplierSearch(p.VendorName || p.SupplierName);
              setSupplierMobile(p.SupplierMobile || '');
            }

            const formattedItems = (p.items || []).map((it) => {
              const r = parseFloat(it.Rate ?? it.rate ?? 0) || 0;
              const q = parseFloat(it.Qty ?? it.qty ?? 0) || 0;
              const t = parseFloat(it.total) || q * r;
              return {
                productId: it.productId || 0,
                productName: it.productName || '',
                qty: q,
                unit: it.unit || 'KG',
                rate: r,
                total: t
              };
            });
            setItems(formattedItems);
          }
        }
      } catch (err) {
        console.error('Error initializing purchase entry:', err);
      }
    };
    loadInitData();
  }, [id]);

  const handleSelectSupplier = async (s) => {
    if (!s) {
      setVendorId('');
      setSupplierSearch('');
      setSupplierMobile('');
      setSelectedSupplierObj(null);
      setSupplierBalance(0);
      return;
    }
    setVendorId(s.Id);
    setSupplierSearch(s.AccountName);
    setSupplierMobile(s.MobileNo || '');
    setSelectedSupplierObj(s);
    setShowSupplierList(false);
    setShowSupplierModal(false);

    try {
      const res = await axiosClient.get(`/accounts/${s.Id}`);
      if (res.success) {
        const acc = res.data;
        setSupplierBalance(parseFloat(acc.financialSummary?.netBalance) || 0);
      }
    } catch (err) {
      console.error('Error fetching supplier balance:', err);
    }
  };

  // Quick Add Supplier directly from Purchase Invoice
  const handleQuickAddSupplier = async (e) => {
    e.preventDefault();
    if (!quickSupplierName || !quickSupplierName.trim()) {
      toast.showError('Please enter Supplier Name.');
      return;
    }

    setQuickSupplierSaving(true);
    try {
      const res = await axiosClient.post('/accounts', {
        accountName: quickSupplierName.trim(),
        accountType: 'SUPPLIER',
        mobileNo: quickSupplierMobile || '0',
        address: quickSupplierAddress || ''
      });

      if (res.success) {
        const newSup = res.data || {
          Id: res.accountId,
          AccountName: quickSupplierName.trim(),
          AccountType: 'SUPPLIER',
          MobileNo: quickSupplierMobile || '',
          Address: quickSupplierAddress || ''
        };

        setSuppliers((prev) => [...prev, newSup]);
        handleSelectSupplier(newSup);
        setSupplierMobile(newSup.MobileNo || '');
        toast.showSuccess(`Supplier "${newSup.AccountName}" saved and selected!`);
        setShowQuickAddSupplierModal(false);
        setQuickSupplierName('');
        setQuickSupplierMobile('');
        setQuickSupplierAddress('');
      }
    } catch (err) {
      toast.showError(err.message || 'Error saving supplier.');
    } finally {
      setQuickSupplierSaving(false);
    }
  };

  const filteredSuppliers = suppliers.filter((s) => {
    const term = (supplierSearch || '').toLowerCase().trim();
    if (!term) return true;
    return (
      (s.AccountName || '').toLowerCase().includes(term) ||
      (s.MobileNo || '').toLowerCase().includes(term) ||
      (s.Address || '').toLowerCase().includes(term)
    );
  });

  // Filtered products list for live search dropdown
  const filteredProducts = products.filter((p) => {
    const term = (currentItem.productName || '').toLowerCase().trim();
    if (!term) return true;
    return (p.productName || '').toLowerCase().includes(term);
  });

  const handleProductSelect = (p) => {
    if (p) {
      setCurrentItem({
        ...currentItem,
        productId: p.Id,
        productName: p.productName,
        unit: p.unit || 'KG',
        rate: parseFloat(p.PRate) || currentItem.rate || 0
      });
      setShowProductList(false);
    } else {
      setCurrentItem({
        ...currentItem,
        productId: '',
        productName: '',
        unit: 'KG',
        rate: 0
      });
    }
  };

  const handleAddItem = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const pName = (currentItem.productName || '').trim();
    if (!pName) {
      alert('Please enter or select a product item.');
      return;
    }
    const q = parseFloat(currentItem.qty) || 0;
    const r = parseFloat(currentItem.rate) || 0;
    if (q <= 0) {
      alert('Please enter valid quantity.');
      return;
    }

    let finalProductId = currentItem.productId;

    // If new product was entered directly, auto-save in product master!
    if (!finalProductId) {
      const matched = products.find(
        (p) => p.productName?.toLowerCase().trim() === pName.toLowerCase()
      );
      if (matched) {
        finalProductId = matched.Id;
      } else {
        try {
          const res = await axiosClient.post('/products', {
            productName: pName,
            sRate: r,
            pRate: r,
            unit: currentItem.unit || 'KG'
          });
          if (res.success) {
            finalProductId = res.productId;
            const newProd = {
              Id: res.productId,
              productName: pName,
              SRate: r,
              PRate: r,
              unit: currentItem.unit || 'KG'
            };
            setProducts((prev) => [...prev, newProd]);
          }
        } catch (err) {
          console.error('Error auto-creating product:', err);
        }
      }
    }

    const newItem = {
      productId: finalProductId || 0,
      productName: pName,
      qty: q,
      unit: currentItem.unit || 'KG',
      rate: r,
      total: q * r
    };

    if (editingItemIndex !== null) {
      const updated = [...items];
      updated[editingItemIndex] = newItem;
      setItems(updated);
      setEditingItemIndex(null);
    } else {
      setItems([...items, newItem]);
    }

    setCurrentItem({
      productId: '',
      productName: '',
      qty: 0,
      unit: 'KG',
      rate: 0
    });
    setShowProductList(false);
    setHighlightedProdIndex(-1);
    setTimeout(() => {
      productInputRef.current?.focus();
    }, 50);
  };

  const handleEditItem = (index) => {
    const it = items[index];
    setCurrentItem({
      productId: it.productId,
      productName: it.productName,
      qty: it.qty,
      unit: it.unit || 'KG',
      rate: it.rate
    });
    setEditingItemIndex(index);
  };

  const handleRemoveItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
    if (editingItemIndex === index) {
      setEditingItemIndex(null);
    }
  };

  const subTotal = items.reduce((sum, it) => sum + (parseFloat(it.total) || 0), 0);
  const calculatedDiscount = (subTotal * (parseFloat(discPercent) || 0)) / 100;
  const grandTotal = subTotal + (parseFloat(vat) || 0) - (parseFloat(discAmount) || calculatedDiscount);

  const handleSavePurchase = async () => {
    const trimmedSupName = (supplierSearch || '').trim();
    if (!trimmedSupName) {
      setError('Please enter supplier name.');
      return;
    }
    if (items.length === 0) {
      setError('Please add at least one line item to purchase bill.');
      return;
    }

    setError('');
    setSaving(true);

    try {
      let finalVendorId = vendorId;

      // If supplier doesn't exist yet, auto-create supplier in database!
      if (!finalVendorId) {
        const matched = suppliers.find(
          (s) => s.AccountName?.trim().toLowerCase() === trimmedSupName.toLowerCase()
        );

        if (matched) {
          finalVendorId = matched.Id;
        } else {
          // Auto create supplier directly
          const createSupRes = await axiosClient.post('/accounts', {
            accountName: trimmedSupName,
            accountType: 'SUPPLIER',
            mobileNo: supplierMobile || '0',
            address: ''
          });

          if (createSupRes.success) {
            finalVendorId = createSupRes.accountId;
            const newSup = createSupRes.data || {
              Id: createSupRes.accountId,
              AccountName: trimmedSupName,
              AccountType: 'SUPPLIER',
              MobileNo: supplierMobile || ''
            };
            setSuppliers((prev) => [newSup, ...prev]);
            setSelectedSupplierObj(newSup);
          } else {
            throw new Error(createSupRes.message || 'Failed to auto-save new supplier.');
          }
        }
      }

      const payload = {
        purchaseNo,
        purchaseDate,
        vendorId: finalVendorId,
        invoiceNo: invoiceNo || '',
        items,
        discPercent,
        discAmount: parseFloat(discAmount) || calculatedDiscount,
        vat,
        invoiceType: parseInt(invoiceType, 10),
        remark
      };

      let savedId = id;
      if (id) {
        await axiosClient.put(`/purchases/${id}`, payload);
        toast.success(`Purchase Voucher #${purchaseNo} updated successfully!`);
      } else {
        const res = await axiosClient.post('/purchases', payload);
        savedId = res.purchaseId;
        toast.success(`Purchase Voucher #${purchaseNo} saved successfully!`);
      }

      // Check WhatsApp Settings for Print Popup & Auto-Send
      let waSettings = null;
      try {
        const waRes = await axiosClient.get('/whatsapp/settings');
        if (waRes.success && waRes.settings) {
          waSettings = waRes.settings;
        }
      } catch (waErr) {
        console.warn('Could not load WhatsApp settings for save flow:', waErr.message);
      }

      const printRes = await axiosClient.get(`/purchases/${savedId}`);
      if (printRes.success) {
        const inv = printRes.data;
        setPrintData({
          ...inv,
          SupplierName: trimmedSupName,
          SupplierMobile: supplierMobile || inv.SupplierMobile
        });
        const shouldShowPopup = !waSettings || (waSettings.ShowPrintPopupPurchase !== 0 && waSettings.ShowPrintPopupPurchase !== false);
        if (shouldShowPopup) {
          setShowPrintModal(true);
        }
      }

      // Auto-send via WhatsApp if enabled in WhatsApp Settings
      try {
        if (waSettings && waSettings.IsEnabled && waSettings.AutoSendPurchase) {
          if (waSettings.SendMode === 'web') {
            const publicUrl = window.location.origin;
            const viewUrl = `${publicUrl}/api/whatsapp/view-invoice?type=purchase&id=${purchaseNo}`;
            const firmName = waSettings.firmName || 'FISH SHOP';
            const msg = `🧾 *PURCHASE VOUCHER #${purchaseNo}*\n🏢 *${firmName}*\nSupplier: ${trimmedSupName}\nTotal Amount: ₹${parseFloat(grandTotal).toFixed(2)}\n📄 *View & Print Voucher:*\n${viewUrl}\n${waSettings.CustomMessage ? '_' + waSettings.CustomMessage + '_' : '🙏 Thank you for your business!'}`;

            const targets = [];
            const supMob = (supplierMobile || printRes.data?.SupplierMobile || '').replace(/\D/g, '');
            if (waSettings.SendToCustomer !== 0 && supMob && supMob.length >= 10) {
              targets.push(supMob.length === 10 ? '91' + supMob : supMob);
            }
            if (waSettings.SendToOwner === 1) {
              const rawOwn = String(waSettings.OwnerMobile || waSettings.firmDefaultContact || '');
              const ownerNums = rawOwn.split(/[,/;\n\s&]+/)
                .map((n) => n.replace(/\D/g, ''))
                .map((n) => (n.length === 12 && n.startsWith('91') ? n.slice(2) : (n.length === 11 && n.startsWith('0') ? n.slice(1) : n)))
                .filter((n) => n.length >= 10);
              ownerNums.forEach((n) => {
                const fullOwn = n.length === 10 ? '91' + n : n;
                if (!targets.includes(fullOwn)) targets.push(fullOwn);
              });
            }

            targets.forEach((phone, idx) => {
              const url = `https://api.whatsapp.com/send?phone=${phone}&text=${encodeURIComponent(msg)}`;
              if (idx === 0) window.open(url, '_blank');
              else setTimeout(() => window.open(url, '_blank'), 1000);
            });
            toast.success('WhatsApp Web / App opened for purchase voucher!');
          } else {
            const sendRes = await axiosClient.post('/whatsapp/send-invoice', {
              purchaseId: savedId,
              invoiceNo: purchaseNo,
              type: 'purchase',
              partyName: trimmedSupName,
              phone: supplierMobile || printRes.data?.SupplierMobile,
              checkSender: true
            });
            if (sendRes.success) {
              toast.success(sendRes.message || 'WhatsApp voucher sent automatically!');
            }
          }
        }
      } catch (waErr) {
        console.warn('Auto-dispatch WhatsApp note:', waErr.message);
      }

      // Automatically reset form to blank ready for next purchase
      await resetFormToBlank();
    } catch (err) {
      const errMsg = err.message || 'Error saving purchase.';
      setError(errMsg);
      toast.error(errMsg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="container-fluid p-0">
      {/* Header Banner */}
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2 pb-2 border-bottom">
        <div>
          <div className="d-flex align-items-center gap-2">
            <h3 className="fw-bold mb-0 text-dark">
              <i className="bi bi-bag-plus-fill text-primary me-2"></i>
              {id ? `Purchase Voucher #${purchaseNo}` : 'Purchase Entry'}
            </h3>
            <span className={`badge ${id ? 'bg-primary' : 'bg-success bg-opacity-10 text-success border border-success'} px-2 py-1`}>
              {id ? 'EDITING VOUCHER' : 'NEW PURCHASE'}
            </span>
            {items.length > 0 && (
              <span className="badge bg-secondary">
                {items.length} {items.length === 1 ? 'Item' : 'Items'}
              </span>
            )}
          </div>
        </div>
        <div className="d-flex gap-2 flex-wrap">
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
            onClick={resetFormToBlank}
            title="Reset Form to Blank"
          >
            <i className="bi bi-arrow-clockwise"></i> New / Blank
          </button>
          <button className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1" onClick={() => navigate('/purchases')}>
            <i className="bi bi-list-ul"></i> Purchases List
          </button>
          <button className="btn btn-ocean btn-sm d-flex align-items-center gap-2 shadow-sm" onClick={handleSavePurchase} disabled={saving}>
            {saving ? <span className="spinner-border spinner-border-sm"></span> : <i className="bi bi-check2-circle fs-6"></i>}
            Save & Print
          </button>
        </div>
      </div>

      {error && <div className="alert alert-danger py-2 small shadow-sm">{error}</div>}

      {/* 1. Supplier & Invoice Details Form */}
      <div className="card shadow-sm border-0 mb-3">
        <div className="card-header bg-light py-2 fw-semibold d-flex justify-content-between align-items-center">
          <span className="form-section-title">
            <i className="bi bi-person-lines-fill"></i> 1. Supplier & Invoice Header
          </span>
          {selectedSupplierObj && (
            <span className="badge bg-info bg-opacity-10 text-primary border border-info small">
              Supplier Code: #{selectedSupplierObj.Id}
            </span>
          )}
        </div>
        <div className="card-body p-3">
          <div className="row g-3 align-items-start">
            <div className="col-6 col-sm-6 col-md-2 col-lg-2">
              <label className="form-label small fw-semibold text-muted">Purchase No *</label>
              <div className="input-group input-group-sm">
                <span className="input-group-text bg-light text-muted">#</span>
                <input
                  type="number"
                  className="form-control fw-bold text-primary"
                  value={purchaseNo}
                  onChange={(e) => setPurchaseNo(e.target.value)}
                />
              </div>
            </div>

            <div className="col-6 col-sm-6 col-md-2 col-lg-2">
              <label className="form-label small fw-semibold text-muted">Purchase Date *</label>
              <input
                type="date"
                className="form-control form-control-sm fw-semibold"
                value={purchaseDate}
                onChange={(e) => setPurchaseDate(e.target.value)}
              />
            </div>

            {/* Searchable Supplier Textbox with List View */}
            <div className="col-12 col-md-4 col-lg-4 position-relative" ref={supplierDropdownRef}>
              <div className="d-flex justify-content-between align-items-center mb-1">
                <label className="form-label small fw-semibold text-muted mb-0">Supplier / Vendor Name *</label>
                {selectedSupplierObj && (
                  <span className={`badge ${supplierBalance < 0 ? 'bg-success text-white' : 'bg-danger text-white'}`}>
                    Outstanding: ₹{Math.abs(supplierBalance).toFixed(2)} {supplierBalance < 0 ? 'Payable' : 'Dr'}
                  </span>
                )}
              </div>

              <div className="input-group input-group-sm">
                <span className="input-group-text bg-white">
                  <i className="bi bi-person text-primary"></i>
                </span>
                <input
                  type="text"
                  className={`form-control form-control-sm ${vendorId ? 'border-primary fw-bold text-dark' : ''}`}
                  placeholder="Enter or search supplier name (↓ to navigate)..."
                  value={supplierSearch}
                  onChange={(e) => {
                    const val = e.target.value;
                    setSupplierSearch(val);
                    setShowSupplierList(true);
                    setHighlightedSupplierIndex(0);
                    if (!val) {
                      handleSelectSupplier(null);
                    } else {
                      const matched = suppliers.find(s => s.AccountName?.toLowerCase().trim() === val.toLowerCase().trim());
                      if (matched) {
                        handleSelectSupplier(matched);
                      } else {
                        setVendorId('');
                        setSelectedSupplierObj(null);
                      }
                    }
                  }}
                  onFocus={() => {
                    setShowSupplierList(true);
                    if (filteredSuppliers.length > 0 && highlightedSupplierIndex === -1) {
                      setHighlightedSupplierIndex(0);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'ArrowDown') {
                      e.preventDefault();
                      if (!showSupplierList) {
                        setShowSupplierList(true);
                        setHighlightedSupplierIndex(0);
                      } else if (filteredSuppliers.length > 0) {
                        setHighlightedSupplierIndex((prev) => (prev < filteredSuppliers.length - 1 ? prev + 1 : 0));
                      }
                    } else if (e.key === 'ArrowUp') {
                      e.preventDefault();
                      if (filteredSuppliers.length > 0) {
                        setHighlightedSupplierIndex((prev) => (prev > 0 ? prev - 1 : filteredSuppliers.length - 1));
                      }
                    } else if (e.key === 'Enter') {
                      e.preventDefault();
                      if (showSupplierList && filteredSuppliers.length > 0) {
                        const targetIdx = highlightedSupplierIndex >= 0 && highlightedSupplierIndex < filteredSuppliers.length ? highlightedSupplierIndex : 0;
                        handleSelectSupplier(filteredSuppliers[targetIdx]);
                        setShowSupplierList(false);
                        setHighlightedSupplierIndex(-1);
                        setTimeout(() => productInputRef.current?.focus(), 50);
                      } else {
                        setShowSupplierList(false);
                        productInputRef.current?.focus();
                      }
                    } else if (e.key === 'Escape') {
                      setShowSupplierList(false);
                    }
                  }}
                  required
                />
                {supplierSearch && (
                  <button
                    className="btn btn-outline-secondary"
                    type="button"
                    onClick={() => {
                      handleSelectSupplier(null);
                      setHighlightedSupplierIndex(-1);
                    }}
                    title="Clear supplier"
                  >
                    <i className="bi bi-x-lg"></i>
                  </button>
                )}
              </div>

              {/* Live Search List View Dropdown - Only shown when matching suppliers exist */}
              {showSupplierList && supplierSearch && filteredSuppliers.length > 0 && (
                <div
                  className="shadow-lg border rounded bg-white position-absolute mt-1"
                  style={{ zIndex: 1050, width: '320px', maxHeight: '280px', overflowY: 'auto' }}
                >
                  <div className="p-2 bg-light border-bottom d-flex justify-content-between align-items-center">
                    <small className="text-muted fw-semibold">
                      Matching Existing Suppliers ({filteredSuppliers.length}) <span className="text-primary small">Use ↑ ↓ & Enter</span>
                    </small>
                    <button
                      type="button"
                      className="btn-close btn-sm"
                      onClick={() => setShowSupplierList(false)}
                    ></button>
                  </div>
                  <div className="list-group list-group-flush">
                    {filteredSuppliers.map((s, idx) => {
                      const isHighlighted = highlightedSupplierIndex === idx;
                      const isSelected = String(vendorId) === String(s.Id);
                      return (
                        <button
                          key={s.Id}
                          type="button"
                          ref={(el) => {
                            if (isHighlighted && el) {
                              el.scrollIntoView({ block: 'nearest' });
                            }
                          }}
                          className={`list-group-item list-group-item-action p-2 d-flex justify-content-between align-items-center ${
                            isHighlighted ? 'bg-primary text-white' : (isSelected ? 'active' : '')
                          }`}
                          onClick={() => {
                            handleSelectSupplier(s);
                            setTimeout(() => productInputRef.current?.focus(), 50);
                          }}
                        >
                          <div>
                            <div className="fw-bold">{s.AccountName}</div>
                            <small className={isHighlighted || isSelected ? 'text-white text-opacity-75' : 'text-muted'}>
                              {s.MobileNo ? `📞 ${s.MobileNo}` : 'No Mobile'} {s.Address ? `• ${s.Address}` : ''}
                            </small>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {selectedSupplierObj && (
                <div className="mt-1 small text-muted d-flex align-items-center flex-wrap gap-2">
                  <span className="badge bg-success bg-opacity-10 text-success border border-success">
                    <i className="bi bi-check-circle me-1"></i> Existing Supplier
                  </span>
                  <span className="fw-bold text-dark">{selectedSupplierObj.AccountName}</span>
                  {supplierMobile && <span className="text-muted">({supplierMobile})</span>}
                  <span className={`badge ${supplierBalance < 0 ? 'bg-success text-white' : 'bg-danger text-white'} px-2 py-1`}>
                    Outstanding Balance: {supplierBalance < 0 ? `₹${Math.abs(supplierBalance).toFixed(2)} (Payable)` : `₹${supplierBalance.toFixed(2)} (Advance/Dr)`}
                  </span>
                </div>
              )}
            </div>

            {/* Supplier Mobile Number Field */}
            <div className="col-12 col-sm-6 col-md-3 col-lg-3">
              <label className="form-label small fw-semibold text-muted">Supplier Mobile</label>
              <div className="input-group input-group-sm">
                <span className="input-group-text bg-light text-muted"><i className="bi bi-telephone"></i></span>
                <input
                  type="tel"
                  className="form-control"
                  placeholder="Mobile number"
                  value={supplierMobile}
                  onChange={(e) => setSupplierMobile(e.target.value)}
                />
              </div>
            </div>

            <div className="col-6 col-sm-6 col-md-2 col-lg-2">
              <label className="form-label small fw-semibold text-muted">Payment Type</label>
              <select
                className="form-select form-select-sm fw-semibold"
                value={invoiceType}
                onChange={(e) => setInvoiceType(e.target.value)}
              >
                <option value="1">Credit</option>
                <option value="0">Cash</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* 2. Item Input Console */}
      <div className="card shadow-sm border-0 mb-3">
        <div className="card-header bg-light py-2 d-flex justify-content-between align-items-center">
          <span className="form-section-title">
            <i className="bi bi-cart-plus-fill"></i>
            2. {editingItemIndex !== null ? `Edit Line #${editingItemIndex + 1}` : 'Add Purchase Line Items'}
          </span>
          {editingItemIndex !== null && (
            <span className="badge bg-warning text-dark">
              <i className="bi bi-pencil-fill me-1"></i> Editing Active Row #{editingItemIndex + 1}
            </span>
          )}
        </div>
        <div className="card-body p-3 item-entry-console">
          <div className="row g-2 align-items-end">
            {/* Searchable Product Input with Live Autocomplete & Direct Entry */}
            <div className="col-12 col-sm-6 col-md-4 col-lg-4 position-relative" ref={productDropdownRef}>
              <label className="form-label small fw-semibold text-muted mb-1">Product / Item *</label>
              <div className="input-group input-group-sm">
                <span className="input-group-text bg-white">
                  <i className="bi bi-search text-teal"></i>
                </span>
                <input
                  type="text"
                  ref={productInputRef}
                  className={`form-control form-control-sm ${currentItem.productId ? 'border-teal fw-bold text-dark' : ''}`}
                  placeholder="Type product name (↓ to navigate)..."
                  value={currentItem.productName}
                  onChange={(e) => {
                    const val = e.target.value;
                    setShowProductList(true);
                    setHighlightedProdIndex(0);
                    const matched = products.find(
                      (p) => p.productName?.toLowerCase().trim() === val.toLowerCase().trim()
                    );
                    if (matched) {
                      setCurrentItem({
                        ...currentItem,
                        productId: matched.Id,
                        productName: matched.productName,
                        unit: matched.unit || currentItem.unit || 'KG',
                        rate: parseFloat(matched.PRate) || currentItem.rate || 0
                      });
                    } else {
                      setCurrentItem({
                        ...currentItem,
                        productId: '',
                        productName: val
                      });
                    }
                  }}
                  onFocus={() => {
                    setShowProductList(true);
                    if (filteredProducts.length > 0 && highlightedProdIndex === -1) {
                      setHighlightedProdIndex(0);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'ArrowDown') {
                      e.preventDefault();
                      if (!showProductList) {
                        setShowProductList(true);
                        setHighlightedProdIndex(0);
                      } else if (filteredProducts.length > 0) {
                        setHighlightedProdIndex((prev) => (prev < filteredProducts.length - 1 ? prev + 1 : 0));
                      }
                    } else if (e.key === 'ArrowUp') {
                      e.preventDefault();
                      if (filteredProducts.length > 0) {
                        setHighlightedProdIndex((prev) => (prev > 0 ? prev - 1 : filteredProducts.length - 1));
                      }
                    } else if (e.key === 'Enter') {
                      e.preventDefault();
                      if (showProductList && filteredProducts.length > 0) {
                        const targetIdx = highlightedProdIndex >= 0 && highlightedProdIndex < filteredProducts.length ? highlightedProdIndex : 0;
                        handleProductSelect(filteredProducts[targetIdx]);
                        setShowProductList(false);
                        setHighlightedProdIndex(-1);
                        setTimeout(() => qtyInputRef.current?.focus(), 50);
                      } else {
                        setShowProductList(false);
                        qtyInputRef.current?.focus();
                      }
                    } else if (e.key === 'Escape') {
                      setShowProductList(false);
                    }
                  }}
                  required
                />
                {currentItem.productName && (
                  <button
                    className="btn btn-outline-secondary"
                    type="button"
                    onClick={() => {
                      setCurrentItem({
                        ...currentItem,
                        productId: '',
                        productName: '',
                        rate: 0
                      });
                      setHighlightedProdIndex(-1);
                    }}
                    title="Clear product"
                  >
                    <i className="bi bi-x-lg"></i>
                  </button>
                )}
              </div>

              {/* Live Product Search Dropdown - Only shown when matching products exist */}
              {showProductList && currentItem.productName && filteredProducts.length > 0 && (
                <div
                  className="shadow-lg border rounded bg-white position-absolute mt-1"
                  style={{ zIndex: 1050, width: '280px', maxHeight: '240px', overflowY: 'auto' }}
                >
                  <div className="p-2 bg-light border-bottom d-flex justify-content-between align-items-center">
                    <small className="text-muted fw-semibold">
                      Matching Fish Items ({filteredProducts.length}) <span className="text-primary small">Use ↑ ↓ & Enter</span>
                    </small>
                    <button
                      type="button"
                      className="btn-close btn-sm"
                      onClick={() => setShowProductList(false)}
                    ></button>
                  </div>
                  <div className="list-group list-group-flush">
                    {filteredProducts.map((p, idx) => {
                      const isHighlighted = highlightedProdIndex === idx;
                      const isSelected = String(currentItem.productId) === String(p.Id);
                      return (
                        <button
                          key={p.Id}
                          type="button"
                          ref={(el) => {
                            if (isHighlighted && el) {
                              el.scrollIntoView({ block: 'nearest' });
                            }
                          }}
                          className={`list-group-item list-group-item-action p-2 d-flex justify-content-between align-items-center ${
                            isHighlighted ? 'bg-primary text-white' : (isSelected ? 'active' : '')
                          }`}
                          onClick={() => {
                            handleProductSelect(p);
                            setTimeout(() => qtyInputRef.current?.focus(), 50);
                          }}
                        >
                          <div className="fw-semibold text-start">{p.productName}</div>
                          <span className={`badge ${isHighlighted ? 'bg-light text-dark' : (isSelected ? 'bg-light text-dark' : 'bg-teal bg-opacity-10 text-teal')} small`}>
                            ₹{parseFloat(p.PRate || 0).toFixed(2)}/{p.unit || 'kg'}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <div className="col-6 col-sm-3 col-md-2 col-lg-2">
              <label className="form-label small fw-semibold text-muted">Quantity (Weight) *</label>
              <input
                type="number"
                step="0.1"
                ref={qtyInputRef}
                className="form-control form-control-sm text-center fw-bold text-primary"
                value={currentItem.qty}
                onChange={(e) => setCurrentItem({ ...currentItem, qty: e.target.value })}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                        e.preventDefault();
                        rateInputRef.current?.focus();
                    }
                }}
              />
            </div>

            <div className="col-6 col-sm-3 col-md-2 col-lg-2">
              <label className="form-label small fw-semibold text-muted">Unit</label>
              <select
                className="form-select form-select-sm text-center fw-semibold"
                value={currentItem.unit}
                onChange={(e) => setCurrentItem({ ...currentItem, unit: e.target.value })}
              >
                <option value="KG">KG</option>
                <option value="BOX">BOX</option>
                <option value="PCS">PCS</option>
              </select>
            </div>

            <div className="col-6 col-sm-6 col-md-2 col-lg-2">
              <label className="form-label small fw-semibold text-muted">Purchase Rate (₹) *</label>
              <input
                type="number"
                step="0.01"
                ref={rateInputRef}
                className="form-control form-control-sm text-end fw-bold text-dark"
                value={currentItem.rate}
                onChange={(e) => setCurrentItem({ ...currentItem, rate: e.target.value })}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddItem(e);
                  }
                }}
              />
            </div>

            <div className="col-6 col-sm-6 col-md-2 col-lg-2 d-flex gap-1">
              <button
                type="button"
                className={`btn btn-sm ${editingItemIndex !== null ? 'btn-warning' : 'btn-teal'} flex-grow-1 d-flex align-items-center justify-content-center gap-1 shadow-sm`}
                onClick={handleAddItem}
              >
                <i className={editingItemIndex !== null ? 'bi bi-check-lg' : 'bi bi-plus-lg'}></i>
                {editingItemIndex !== null ? 'Update Item' : 'Add Item'}
              </button>
              {editingItemIndex !== null && (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => {
                    setEditingItemIndex(null);
                    setCurrentItem({
                      productId: '',
                      productName: '',
                      qty: 0,
                      unit: 'KG',
                      rate: 0
                    });
                  }}
                  title="Cancel Edit"
                >
                  <i className="bi bi-x-lg"></i>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Line Items Table */}
      <div className="card shadow-sm border-0 mb-3">
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-bordered table-hover table-sm align-middle mb-0">
              <thead className="table-light text-center small">
                <tr>
                  <th style={{ width: '40px' }}>#</th>
                  <th className="text-start">Product / Item</th>
                  <th>Quantity</th>
                  <th>Unit</th>
                  <th className="text-end">Rate (₹)</th>
                  <th className="text-end">Total Amount (₹)</th>
                  <th style={{ width: '80px' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center py-4 text-muted small">
                      <i className="bi bi-inbox fs-3 d-block text-secondary mb-1"></i>
                      No purchase items added yet. Select a product above and click "Add Item".
                    </td>
                  </tr>
                ) : (
                  items.map((it, idx) => (
                    <tr key={idx} className={`text-center small ${editingItemIndex === idx ? 'table-warning' : ''}`}>
                      <td className="text-muted">{idx + 1}</td>
                      <td className="text-start fw-bold text-dark">{it.productName}</td>
                      <td className="fw-semibold text-primary">{parseFloat(it.qty).toFixed(2)}</td>
                      <td><span className="badge bg-light text-dark border">{it.unit}</span></td>
                      <td className="text-end fw-semibold">₹{parseFloat(it.rate).toFixed(2)}</td>
                      <td className="text-end fw-bold text-success">₹{parseFloat(it.total).toFixed(2)}</td>
                      <td>
                        <div className="btn-group btn-group-sm">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary py-0 px-2"
                            onClick={() => handleEditItem(idx)}
                            title="Edit this line"
                          >
                            <i className="bi bi-pencil"></i>
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger py-0 px-2"
                            onClick={() => handleRemoveItem(idx)}
                            title="Remove this line"
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              {items.length > 0 && (
                <tfoot className="table-light small fw-bold">
                  <tr className="text-center">
                    <td colSpan="2" className="text-end text-muted">Summary Totals:</td>
                    <td className="text-primary">{items.reduce((s, it) => s + (parseFloat(it.qty) || 0), 0).toFixed(2)}</td>
                    <td></td>
                    <td className="text-end text-muted">Subtotal:</td>
                    <td className="text-end text-success fs-6">₹{subTotal.toFixed(2)}</td>
                    <td></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      </div>

      {/* Summary Footer */}
      <div className="row g-3 mb-4">
        <div className="col-12 col-lg-7">
          <div className="card shadow-sm border-0 h-100 p-3">
            <div className="mb-2">
              <label className="form-label small fw-semibold text-muted">
                <i className="bi bi-chat-left-text me-1"></i> Purchase Notes & Jetty Remarks
              </label>
              <textarea
                className="form-control form-control-sm"
                rows="4"
                placeholder="Boat / vessel arrival details, quality notes, landing jetty..."
                value={remark}
                onChange={(e) => setRemark(e.target.value)}
              ></textarea>
            </div>
          </div>
        </div>

        <div className="col-12 col-lg-5">
          <div className="summary-panel-card bg-white h-100">
            <div className="summary-panel-header d-flex justify-content-between align-items-center">
              <span><i className="bi bi-wallet2 me-2"></i> Bill Summary</span>
              <span className="badge bg-white text-dark small">₹ INR</span>
            </div>
            <div className="card-body p-0">
              <div className="summary-row">
                <span className="text-muted">Items Subtotal:</span>
                <span className="fw-bold">₹{subTotal.toFixed(2)}</span>
              </div>
              <div className="summary-row">
                <span className="text-muted">Discount (%):</span>
                <div className="input-group input-group-sm w-50">
                  <span className="input-group-text">%</span>
                  <input
                    type="number"
                    step="0.1"
                    className="form-control text-end fw-semibold"
                    value={discPercent}
                    onChange={(e) => {
                      setDiscPercent(e.target.value);
                      setDiscAmount((subTotal * (parseFloat(e.target.value) || 0)) / 100);
                    }}
                  />
                </div>
              </div>
              <div className="summary-row">
                <span className="text-muted">Discount Amount:</span>
                <span className="text-danger fw-semibold">-₹{(parseFloat(discAmount) || calculatedDiscount).toFixed(2)}</span>
              </div>

              <div className="summary-row grand-total">
                <span>Grand Total (Payable):</span>
                <span>₹{grandTotal.toFixed(2)}</span>
              </div>

              <div className="p-3">
                <button
                  type="button"
                  className="btn btn-ocean btn-lg w-100 shadow-sm d-flex align-items-center justify-content-center gap-2"
                  onClick={handleSavePurchase}
                  disabled={saving}
                >
                  {saving ? <span className="spinner-border spinner-border-sm"></span> : <i className="bi bi-printer-fill fs-5"></i>}
                  Save & Print Voucher
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Supplier List View Modal */}
      {showSupplierModal && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg modal-dialog-scrollable">
            <div className="modal-content shadow-lg border-0">
              <div className="modal-header bg-primary text-white py-2">
                <h5 className="modal-title fw-bold">
                  <i className="bi bi-people-fill me-2"></i> Select Supplier / Vendor (List View)
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setShowSupplierModal(false)}
                ></button>
              </div>
              <div className="modal-body p-3">
                <div className="mb-3">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search by supplier name, mobile, address..."
                    value={supplierSearch}
                    onChange={(e) => setSupplierSearch(e.target.value)}
                    autoFocus
                  />
                </div>
                <div className="table-responsive" style={{ maxHeight: '400px' }}>
                  <table className="table table-hover align-middle mb-0">
                    <thead className="table-light sticky-top">
                      <tr>
                        <th>#</th>
                        <th>Supplier Name</th>
                        <th>Mobile</th>
                        <th>Address</th>
                        <th className="text-center">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredSuppliers.length === 0 ? (
                        <tr>
                          <td colSpan="5" className="text-center py-4 text-muted">
                            No matching suppliers found.
                          </td>
                        </tr>
                      ) : (
                        filteredSuppliers.map((s, idx) => (
                          <tr
                            key={s.Id}
                            className={String(vendorId) === String(s.Id) ? 'table-primary fw-bold' : ''}
                            style={{ cursor: 'pointer' }}
                            onClick={() => handleSelectSupplier(s)}
                          >
                            <td>{idx + 1}</td>
                            <td className="text-primary fw-bold">{s.AccountName}</td>
                            <td>{s.MobileNo || '-'}</td>
                            <td>{s.Address || '-'}</td>
                            <td className="text-center">
                              <button
                                type="button"
                                className="btn btn-sm btn-ocean py-0 px-2"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSelectSupplier(s);
                                }}
                              >
                                Select
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="modal-footer py-2 bg-light">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowSupplierModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quick Add Supplier Modal */}
      {showQuickAddSupplierModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content shadow-lg border-0">
              <div className="modal-header bg-success text-white py-3">
                <h5 className="modal-title fw-bold">
                  <i className="bi bi-person-plus-fill me-2"></i> Quick Add New Supplier
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setShowQuickAddSupplierModal(false)}
                ></button>
              </div>
              <form onSubmit={handleQuickAddSupplier}>
                <div className="modal-body p-4">
                  <div className="mb-3">
                    <label className="form-label fw-semibold small text-muted">
                      Supplier / Vendor Name <span className="text-danger">*</span>
                    </label>
                    <div className="input-group">
                      <span className="input-group-text bg-light"><i className="bi bi-person-fill"></i></span>
                      <input
                        type="text"
                        className="form-control fw-bold"
                        placeholder="e.g. Kasim Fishermen / Alibag Jetty Boat"
                        value={quickSupplierName}
                        onChange={(e) => setQuickSupplierName(e.target.value)}
                        required
                        autoFocus
                      />
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-semibold small text-muted">
                      Mobile Number <span className="text-danger">*</span>
                    </label>
                    <div className="input-group">
                      <span className="input-group-text bg-light"><i className="bi bi-telephone-fill"></i></span>
                      <input
                        type="tel"
                        className="form-control"
                        placeholder="10-digit Mobile No"
                        value={quickSupplierMobile}
                        onChange={(e) => setQuickSupplierMobile(e.target.value)}
                        required
                      />
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-semibold small text-muted">Address / Harbor / Jetty</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="e.g. Sassoon Dock / Bhaucha Dhakka"
                      value={quickSupplierAddress}
                      onChange={(e) => setQuickSupplierAddress(e.target.value)}
                    />
                  </div>
                </div>

                <div className="modal-footer py-2 bg-light">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowQuickAddSupplierModal(false)}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-success d-flex align-items-center gap-2" disabled={quickSupplierSaving}>
                    {quickSupplierSaving ? (
                      <span className="spinner-border spinner-border-sm" role="status"></span>
                    ) : (
                      <i className="bi bi-check-circle-fill"></i>
                    )}
                    Save & Select Supplier
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Printable Voucher Modal */}
      {showPrintModal && printData && (
        <InvoicePrintModal
          invoiceData={printData}
          type="PURCHASE"
          onClose={() => {
            setShowPrintModal(false);
            navigate('/purchases');
          }}
        />
      )}
    </div>
  );
};

export default PurchaseEntry;
